import { useState, useEffect } from 'react'
import { MultistepDialog, StepConfig } from '@/components/ui/multistep-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { FileText, Loader2, Home } from 'lucide-react'
import { toast } from 'sonner'
import type { PropertyCSVProcessingResult } from '@/lib/csv-upload/property-csv-processor'
import { PropertyCSVProcessor } from '@/lib/csv-upload/property-csv-processor'
import type { UnitCSVProcessingResult } from '@/lib/csv-upload/unit-csv-processor'
import { UnitCSVProcessor } from '@/lib/csv-upload/unit-csv-processor'
import { useAppAuth } from '@/contexts/app-auth.context'
import { propertyResolutionApiClient, unitResolutionApiClient } from '@/lib/api'
import type { PropertyResolutionPlan } from '@/lib/api/property-resolution.client'
import type { UnitResolutionPlan } from '@/lib/api/unit-resolution.client'
import type { Property } from '@super/shared-types'
import { IntegrationSource } from '@super/shared-types'
import {
  CSVFileUploader,
  PMSSelector,
  CSVPreviewTable,
  CSVColumnHeaders,
  CSVValidationSummary,
} from './shared'

type CsvType = 'properties' | 'units'

interface PropertyCSVUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploadComplete?: () => void
  preloadedFile: File | null
  onFileChange: (file: File | null) => void
  existingProperties?: Property[]
  preselectedCsvType?: CsvType
}

export function PropertyCSVUploadDialog({
  open,
  onOpenChange,
  onUploadComplete,
  preloadedFile,
  onFileChange,
  existingProperties = [],
  preselectedCsvType,
}: PropertyCSVUploadDialogProps) {
  const { currentCompany } = useAppAuth()
  const [currentStep, setCurrentStep] = useState(0)

  const [csvType, setCsvType] = useState<CsvType>(
    preselectedCsvType ?? 'properties',
  )
  const [pmsType, setPMSType] = useState<IntegrationSource | ''>(
    IntegrationSource.APPFOLIO,
  )
  const [isProcessing, setIsProcessing] = useState(false)
  const [, setError] = useState<string | null>(null)
  const [propertyResult, setPropertyResult] =
    useState<PropertyCSVProcessingResult | null>(null)
  const [unitResult, setUnitResult] = useState<UnitCSVProcessingResult | null>(
    null,
  )

  useEffect(() => {
    if (preselectedCsvType) {
      setCsvType(preselectedCsvType)
    }
  }, [preselectedCsvType])

  const processingResult = csvType === 'units' ? unitResult : propertyResult

  const handleCsvTypeChange = (value: CsvType) => {
    setCsvType(value)
    setPropertyResult(null)
    setUnitResult(null)
  }

  const handleCancel = () => {
    setCsvType(preselectedCsvType ?? 'properties')
    setPMSType(IntegrationSource.APPFOLIO)
    setIsProcessing(false)
    setError(null)
    setPropertyResult(null)
    setUnitResult(null)
    setCurrentStep(0)
    onFileChange?.(null)
    onOpenChange(false)
  }

  const handleStepChange = async (step: number) => {
    setCurrentStep(step)
    if (step === 1 && !processingResult) {
      await processFile()
    }
  }

  const processFile = async () => {
    if (!preloadedFile || !pmsType || !currentCompany) {
      setError('Missing required configuration')
      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      if (csvType === 'units') {
        const processor = new UnitCSVProcessor(pmsType, currentCompany.id)
        const result = await processor.processFile(preloadedFile)
        setUnitResult(result)
      } else {
        const processor = new PropertyCSVProcessor(pmsType, currentCompany.id)
        const result = await processor.processFile(
          preloadedFile,
          existingProperties,
        )
        setPropertyResult(result)
      }
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'Failed to process file',
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const handleComplete = async () => {
    if (!currentCompany || !pmsType) {
      setError('Missing required configuration')
      return
    }

    if (csvType === 'units') {
      if (!unitResult) {
        setError('No processing result found')
        return
      }

      try {
        toast.info('Creating units...', {
          description: 'Adding units to your database',
        })

        const resolutionPlan: UnitResolutionPlan = {
          units: unitResult.units.map(unit => ({
            externalId: unit.externalId,
            name: unit.name,
            unitAddress: unit.unitAddress,
            propertyExternalId: unit.propertyExternalId,
          })),
          metadata: {
            source: pmsType,
            uploadedAt: new Date().toISOString(),
            totalCount: unitResult.units.length,
          },
        }

        const response = await unitResolutionApiClient.executeUnitResolution({
          unitResolutionPlan: resolutionPlan,
          companyId: currentCompany.id,
          options: { updateExisting: true, continueOnError: true },
        })

        if (response.success) {
          toast.success('Unit upload completed!', {
            description: `Created ${response.results.createdUnits}, updated ${response.results.updatedUnits}${response.results.unmatchedProperties ? `, ${response.results.unmatchedProperties} unmatched properties` : ''}`,
            duration: 5000,
          })
          onUploadComplete?.()
          setTimeout(() => handleCancel(), 500)
        } else {
          throw new Error('Unit resolution failed')
        }
      } catch (error) {
        console.error('Failed to create units:', error)
        toast.error('Upload failed', {
          description: 'Failed to create units. Please try again.',
        })
      }
    } else {
      if (!propertyResult) {
        setError('No processing result found')
        return
      }

      try {
        toast.info('Creating properties...', {
          description: 'Adding properties to your database',
        })

        const resolutionPlan: PropertyResolutionPlan = {
          properties: propertyResult.properties.map(property => ({
            externalId: property.externalId,
            name: property.name,
            propertyAddress: {
              addressStreet1: property.propertyAddress.addressStreet1,
              addressStreet2: property.propertyAddress.addressStreet2,
              addressCity: property.propertyAddress.addressCity,
              addressState: property.propertyAddress.addressState,
              addressPostcode: property.propertyAddress.addressPostcode,
            },
            propertyType: property.propertyType ? [property.propertyType] : [],
          })),
          metadata: {
            source: pmsType,
            uploadedAt: new Date().toISOString(),
            totalCount: propertyResult.properties.length,
          },
        }

        const response =
          await propertyResolutionApiClient.executePropertyResolution({
            propertyResolutionPlan: resolutionPlan,
            companyId: currentCompany.id,
            options: { updateExisting: true, continueOnError: true },
          })

        if (response.success) {
          toast.success('Property upload completed! 🏠', {
            description: `Created ${response.results.createdProperties} properties, updated ${response.results.updatedProperties} properties`,
            duration: 5000,
          })
          onUploadComplete?.()
          setTimeout(() => handleCancel(), 500)
        } else {
          throw new Error('Property resolution failed')
        }
      } catch (error) {
        console.error('Failed to create properties:', error)
        toast.error('Upload failed', {
          description: 'Failed to create properties. Please try again.',
        })
      }
    }
  }

  const step1CanProceed = Boolean(preloadedFile && pmsType)

  const step1Content = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>CSV type</Label>
        <Select value={csvType} onValueChange={handleCsvTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select CSV type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="properties">Properties</SelectItem>
            <SelectItem value="units">Units</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <PMSSelector
        value={pmsType}
        onValueChange={setPMSType}
        disabled={isProcessing}
      />

      <CSVFileUploader
        uploadedFile={preloadedFile}
        onFileChange={onFileChange}
        disabled={isProcessing}
      />
    </div>
  )

  const step2Content = (
    <div className="space-y-4">
      {isProcessing ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>
            Analyzing {csvType === 'units' ? 'unit' : 'property'} CSV file...
          </span>
        </div>
      ) : processingResult ? (
        <div className="space-y-4">
          {processingResult.headers && processingResult.headers.length > 0 && (
            <CSVColumnHeaders
              headers={processingResult.headers}
              pmsType={pmsType || IntegrationSource.APPFOLIO}
              uploadType={csvType}
            />
          )}
          {processingResult.sampleRows &&
            processingResult.sampleRows.length > 0 && (
              <CSVPreviewTable
                headers={processingResult.headers || []}
                sampleRows={processingResult.sampleRows}
                pmsType={pmsType || IntegrationSource.APPFOLIO}
                uploadType={csvType}
              />
            )}
          <CSVValidationSummary
            success={processingResult.success}
            totalRows={processingResult.totalRows}
            processedRows={processingResult.processedRows}
            skipped={processingResult.skipped}
            errors={processingResult.errors}
            warnings={processingResult.warnings}
            entityType={csvType}
          />
        </div>
      ) : null}
    </div>
  )

  const step3Content =
    csvType === 'units' ? (
      <div className="space-y-4">
        {unitResult ? (
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2">
              <h4 className="font-medium text-sm text-gray-800">
                Unit Resolution Summary
              </h4>
              <div className="flex justify-between text-sm text-gray-700">
                <span>Total units to upsert:</span>
                <span className="font-medium">{unitResult.units.length}</span>
              </div>
              {unitResult.warnings.length > 0 && (
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Rows skipped:</span>
                  <span className="font-medium">
                    {unitResult.warnings.length}
                  </span>
                </div>
              )}
            </div>
            <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 p-3 rounded-md">
              ✓ Ready to process {unitResult.units.length} units. Click "Create
              units" to proceed.
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <FileText className="h-6 w-6 mr-2" />
            <span>Resolution details will appear here</span>
          </div>
        )}
      </div>
    ) : (
      <div className="space-y-4">
        {propertyResult ? (
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2">
              <h4 className="font-medium text-sm text-gray-800">
                Property Resolution Summary
              </h4>
              <div className="flex justify-between text-sm text-gray-700">
                <span>Total properties found:</span>
                <span className="font-medium">
                  {propertyResult.properties.length}
                </span>
              </div>
              {propertyResult.duplicateDetection && (
                <>
                  <div className="flex justify-between text-sm text-gray-700">
                    <span>Properties to create:</span>
                    <span className="font-medium">
                      {propertyResult.duplicateDetection.toCreate}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-700">
                    <span>Properties to update:</span>
                    <span className="font-medium">
                      {propertyResult.duplicateDetection.toUpdate}
                    </span>
                  </div>
                </>
              )}
              {propertyResult.warnings.length > 0 && (
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Rows skipped (validation issues):</span>
                  <span className="font-medium">
                    {propertyResult.warnings.length}
                  </span>
                </div>
              )}
            </div>

            {propertyResult.duplicateDetection &&
              propertyResult.duplicateDetection.results.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">
                    Properties to be processed:
                  </h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {propertyResult.duplicateDetection.results
                      .slice(0, 3)
                      .map((result, index) => (
                        <div
                          key={index}
                          className="p-3 rounded-md border bg-gray-50 border-gray-200"
                        >
                          <div className="flex items-center space-x-2">
                            <Home className="h-4 w-4 text-gray-600" />
                            <div>
                              <div className="flex items-center space-x-2">
                                <div className="font-medium text-sm text-gray-900">
                                  {result.csvProperty.name}
                                </div>
                                <span className="text-xs px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 border border-gray-200">
                                  {result.operation === 'create'
                                    ? 'CREATE'
                                    : 'UPDATE'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600">
                                {
                                  result.csvProperty.propertyAddress
                                    .addressStreet1
                                }
                                ,{' '}
                                {result.csvProperty.propertyAddress.addressCity}
                                ,{' '}
                                {
                                  result.csvProperty.propertyAddress
                                    .addressState
                                }
                              </div>
                              <div className="text-xs text-gray-500">
                                ID: {result.csvProperty.externalId} • Type:{' '}
                                {result.csvProperty.propertyType}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    {propertyResult.duplicateDetection.results.length > 3 && (
                      <div className="text-xs text-gray-500 text-center">
                        ... and{' '}
                        {propertyResult.duplicateDetection.results.length - 3}{' '}
                        more properties
                      </div>
                    )}
                  </div>
                </div>
              )}

            {propertyResult.duplicateDetection &&
              propertyResult.duplicateDetection.results.length > 0 && (
                <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 p-3 rounded-md">
                  ✓ Ready to process{' '}
                  {propertyResult.duplicateDetection.results.length} properties
                  ({propertyResult.duplicateDetection.toCreate} creates,{' '}
                  {propertyResult.duplicateDetection.toUpdate} updates). Click
                  "Create properties" to proceed.
                </div>
              )}
          </div>
        ) : (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <FileText className="h-6 w-6 mr-2" />
            <span>Resolution details will appear here</span>
          </div>
        )}
      </div>
    )

  const stepConfigs: StepConfig[] = [
    {
      id: 'configure',
      title: `Upload ${csvType}`,
      content: step1Content,
      canProceed: step1CanProceed,
      nextText: 'Preview',
    },
    {
      id: 'preview',
      title: 'Preview & validate',
      content: step2Content,
      canProceed: processingResult?.success || false,
      nextText: 'Review',
    },
    {
      id: 'resolution',
      title: 'Review & create',
      content: step3Content,
      canProceed: processingResult?.success || false,
      nextText: csvType === 'units' ? 'Create units' : 'Create properties',
    },
  ]

  return (
    <MultistepDialog
      open={open}
      onOpenChange={onOpenChange}
      steps={stepConfigs}
      currentStep={currentStep}
      onStepChange={handleStepChange}
      onComplete={handleComplete}
      onCancel={handleCancel}
      className="max-w-xl"
    />
  )
}
